import { PageHeader, Select } from '@components';
import { TableFooter } from '@components/Table/TableFooter';
import BudgetExpenseLineAPICaller from '@services/api/budgetExpenseLine';
import CostCenterAPICaller from '@services/api/costCenter';
import { BudgetExpenseDraftSummary } from 'interfaces/BudgetExpenseLine';
import { CostCenter } from 'interfaces/CostCenter';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formatCurrency } from 'utils/string/currency';

import '../BudgetExpenseGridPage/expenseGrid.css';

const MONTHS: { key: string; label: string }[] = [
  { key: 'january', label: 'Jan' },
  { key: 'february', label: 'Fev' },
  { key: 'march', label: 'Mar' },
  { key: 'april', label: 'Abr' },
  { key: 'may', label: 'Mai' },
  { key: 'june', label: 'Jun' },
  { key: 'july', label: 'Jul' },
  { key: 'august', label: 'Ago' },
  { key: 'september', label: 'Set' },
  { key: 'october', label: 'Out' },
  { key: 'november', label: 'Nov' },
  { key: 'december', label: 'Dez' },
];

type Quarter = 'q1' | 'q2' | 'q3' | 'q4';

const QUARTER_MONTHS: Record<Quarter, string[]> = {
  q1: ['january', 'february', 'march'],
  q2: ['april', 'may', 'june'],
  q3: ['july', 'august', 'september'],
  q4: ['october', 'november', 'december'],
};

// Month at the end of each quarter — drops a Q subtotal column right after it.
const QUARTER_BOUNDARY: { [month: string]: Quarter } = {
  march: 'q1',
  june: 'q2',
  september: 'q3',
  december: 'q4',
};

const QUARTER_OPTIONS: { label: string; value: '' | Quarter }[] = [
  { label: 'Ano inteiro', value: '' },
  { label: 'Q1', value: 'q1' },
  { label: 'Q2', value: 'q2' },
  { label: 'Q3', value: 'q3' },
  { label: 'Q4', value: 'q4' },
];

const HISTORY_START = 2025;
const PAGE_SIZE = 10;

const centsToCurrency = (cents?: number) =>
  formatCurrency((Number(cents) || 0) / 100) ?? '';

// Zeros drop to a faint dash so a wide row of figures reads as hierarchy.
const renderSummaryValue = (cents?: number) => {
  const value = Number(cents) || 0;
  if (value === 0) return <span className="cell-zero">–</span>;
  return centsToCurrency(value);
};

const EMPTY_DRAFT_SUMMARY: BudgetExpenseDraftSummary = {
  rows: [],
  total: 0,
  totals: {
    months: {},
    quarters: { q1: 0, q2: 0, q3: 0, q4: 0 },
    totalCents: 0,
  },
};

type Totals = {
  months: { [month: string]: number };
  quarters: { q1: number; q2: number; q3: number; q4: number };
  totalCents: number;
};

export default function BudgetExpenseDraftSummaryPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('');
  const [quarter, setQuarter] = useState<'' | Quarter>('');
  const [page, setPage] = useState(1);
  const [draftSummary, setDraftSummary] =
    useState<BudgetExpenseDraftSummary>(EMPTY_DRAFT_SUMMARY);
  const [isLoading, setIsLoading] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setIsLoading(true);
    BudgetExpenseLineAPICaller.draftSummary({
      year,
      costCenterId: costCenterFilter || undefined,
      search: debouncedSearch.trim() || undefined,
      page,
      size: PAGE_SIZE,
    })
      .then(setDraftSummary)
      .catch((error) => {
        // Clear rather than keep the previous filter's numbers on screen —
        // stale totals here read as real money.
        setDraftSummary(EMPTY_DRAFT_SUMMARY);
        const message =
          error?.response?.data?.message || 'Erro ao carregar o resumo.';
        toast.error(message);
      })
      .finally(() => setIsLoading(false));
  }, [year, costCenterFilter, debouncedSearch, page]);

  useEffect(() => {
    CostCenterAPICaller.listAll().then((values) =>
      setCostCenters(values.result || [])
    );
  }, []);

  const yearOptions = Array.from(
    { length: currentYear + 1 - HISTORY_START + 1 },
    (_, i) => HISTORY_START + i
  ).map((y) => ({ label: String(y), value: String(y) }));

  const costCenterOptions = [
    { label: 'Todos os centros de custo', value: '' },
    ...costCenters.map((c) => ({ label: c.name, value: c.id as string })),
  ];

  const rows = draftSummary.rows;
  const totals: Totals = draftSummary.totals;
  const pageCount = Math.max(1, Math.ceil(draftSummary.total / PAGE_SIZE));

  const visibleMonths = quarter
    ? MONTHS.filter((m) => QUARTER_MONTHS[quarter].includes(m.key))
    : MONTHS;
  const showQuarterSubtotals = !quarter;
  const trailingLabel = quarter ? `Total ${quarter.toUpperCase()}` : 'Anual';
  const trailingValue = (entity: Totals) =>
    quarter ? entity.quarters[quarter] : entity.totalCents;
  const colSpan = visibleMonths.length + (showQuarterSubtotals ? 4 : 0) + 2;

  return (
    <div className="p-s-300">
      <PageHeader title="Resumo e revisão de despesas" />

      <p className="expense-intro">
        Soma das linhas de despesa ainda <strong>Em revisão</strong> (não
        fechadas), por centro de custo e categoria. Os trimestres são
        calculados a partir dos meses — o valor real de cada trimestre só passa
        a existir depois que a revisão é fechada na grade mensal.
      </p>

      <div
        className="expense-grid-filters d-flex mt-s-200"
        style={{ gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div style={{ minWidth: 220 }}>
          <div className="form-input__container">
            <label className="form-input__label">Buscar</label>
            <div className="form-input__addons__container">
              <input
                className="form-input"
                placeholder="Centro de custo ou categoria"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div style={{ minWidth: 240 }}>
          <Select
            label="Centro de custo"
            value={costCenterOptions.find((o) => o.value === costCenterFilter)}
            onSelect={(option: { label: string; value: string }) => {
              setCostCenterFilter(option.value);
              setPage(1);
            }}
            options={costCenterOptions}
          />
        </div>
        <div style={{ minWidth: 140 }}>
          <Select
            label="Período"
            value={QUARTER_OPTIONS.find((o) => o.value === quarter)}
            onSelect={(option: { label: string; value: '' | Quarter }) =>
              setQuarter(option.value)
            }
            options={QUARTER_OPTIONS}
          />
        </div>
        <div style={{ minWidth: 140 }}>
          <Select
            label="Ano"
            value={{ label: String(year), value: String(year) }}
            onSelect={(option: { label: string; value: string }) => {
              setYear(Number(option.value));
              setPage(1);
            }}
            options={yearOptions}
          />
        </div>
      </div>

      <section className="expense-section">
        <div className="expense-section__head">
          <div>
            <h3 className="expense-section__title">Por centro de custo</h3>
            <p className="expense-section__caption">
              Cada centro de custo abre nas suas categorias.
            </p>
          </div>
          <span className="expense-section__meta">Ano {year}</span>
        </div>
        {isLoading ? (
          <p className="p-s-200">Carregando...</p>
        ) : (
          <div className="expense-grid-wrapper">
            <table className="expense-grid-table summary-table">
              <thead>
                <tr>
                  <th className="sticky-col col-name">Centro de custo</th>
                  {visibleMonths.map((m) => (
                    <Fragment key={m.key}>
                      <th className="month-col">{m.label}</th>
                      {showQuarterSubtotals && QUARTER_BOUNDARY[m.key] && (
                        <th className="quarter-col">
                          {QUARTER_BOUNDARY[m.key].toUpperCase()}
                        </th>
                      )}
                    </Fragment>
                  ))}
                  <th className="annual-col">{trailingLabel}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="empty">
                      Nenhuma linha em revisão para os filtros atuais.
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <Fragment key={row.costCenterId}>
                    <tr className="group-row">
                      <td className="sticky-col col-name">
                        {row.costCenterName}
                      </td>
                      {visibleMonths.map((m) => (
                        <Fragment key={m.key}>
                          <td className="month-col">
                            {renderSummaryValue(row.months[m.key])}
                          </td>
                          {showQuarterSubtotals && QUARTER_BOUNDARY[m.key] && (
                            <td className="quarter-col">
                              {renderSummaryValue(
                                row.quarters[QUARTER_BOUNDARY[m.key]]
                              )}
                            </td>
                          )}
                        </Fragment>
                      ))}
                      <td className="annual-col">
                        {renderSummaryValue(trailingValue(row))}
                      </td>
                    </tr>
                    {row.categories.map((category) => (
                      <tr className="detail-row" key={category.categoryId}>
                        <td
                          className="sticky-col col-name"
                          style={{ paddingLeft: '1.75rem' }}
                        >
                          {category.categoryName}
                        </td>
                        {visibleMonths.map((m) => (
                          <Fragment key={m.key}>
                            <td className="month-col">
                              {renderSummaryValue(category.months[m.key])}
                            </td>
                            {showQuarterSubtotals &&
                              QUARTER_BOUNDARY[m.key] && (
                                <td className="quarter-col">
                                  {renderSummaryValue(
                                    category.quarters[QUARTER_BOUNDARY[m.key]]
                                  )}
                                </td>
                              )}
                          </Fragment>
                        ))}
                        <td className="annual-col">
                          {renderSummaryValue(trailingValue(category))}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
                {draftSummary.total > 0 && (
                  <tr className="grand-total-row">
                    <td className="sticky-col col-name">Total geral</td>
                    {visibleMonths.map((m) => (
                      <Fragment key={m.key}>
                        <td className="month-col">
                          {renderSummaryValue(totals.months[m.key])}
                        </td>
                        {showQuarterSubtotals && QUARTER_BOUNDARY[m.key] && (
                          <td className="quarter-col">
                            {renderSummaryValue(
                              totals.quarters[QUARTER_BOUNDARY[m.key]]
                            )}
                          </td>
                        )}
                      </Fragment>
                    ))}
                    <td className="annual-col">
                      {renderSummaryValue(trailingValue(totals))}
                    </td>
                  </tr>
                )}
              </tbody>
              {pageCount > 1 && (
                <TableFooter
                  currentPage={page}
                  pageCount={pageCount}
                  setCurrentPage={setPage}
                  onPageChange={(newPage) => setPage(newPage)}
                  colSpan={colSpan}
                />
              )}
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
